import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useToastContext } from "../contexts/ToastContext";
import { prettyFieldLabel } from "../utils/labels";
import {
  fetchScenarios,
  fetchDocuments,
  updateTenantReimbursement,
  autoProject,
  fetchDeal,
  type Deal,
  type ScenarioResponse,
  type ReimbursementDetail,
  type CamStructure,
  type DocumentMeta,
} from "../api/client";
import { useDealContext } from "../hooks/useDealContext";
import LoadingSpinner from "../components/LoadingSpinner";
import { SkeletonTable } from "../components/SkeletonLoader";
import EmptyState from "../components/EmptyState";
import ReimbursementEditor, {
  CamBadge,
  Field,
  type PoolReimbursement,
  type TenantSummary,
} from "../components/ReimbursementEditor";
import ExpensePoolsView from "../components/ExpensePoolEditor";
import BulkReimbursementToolbar from "../components/BulkReimbursementToolbar";
import {
  RecoveryRateTrendChart,
  TenantRecoveryComparisonTable,
  ViewModeToggle,
  type ViewMode,
} from "../components/ReimbursementAnalysisExtras";
import { Calculator, Printer, Upload, Settings, Download, FileText, X } from "lucide-react";
import { fmtCurrency, fmtCurrencyAcct, fmtPercent, fmtPSF, fmtCamType } from "../utils/format";
import { downloadCSV } from "../utils/csv";

export default function ReimbursementsPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const [searchParams] = useSearchParams();
  const { deal, setDeal } = useDealContext();
  const { addToast } = useToastContext();
  const [scenarios, setScenarios] = useState<ScenarioResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const hasTriedAutoProject = useRef(false);

  useEffect(() => {
    if (deal.property_data || !dealId || hasTriedAutoProject.current) return;
    hasTriedAutoProject.current = true;
    autoProject(dealId)
      .then(() => fetchDeal(dealId))
      .then(setDeal)
      .catch(() => {});
  }, [deal.property_data, dealId, setDeal]);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [expandedTenant, setExpandedTenant] = useState<string | null>(null);

  // Define mode state
  const targetTenant = searchParams.get("tenant");
  const [mode, setMode] = useState<"analysis" | "define" | "pools">(
    targetTenant ? "define" : "analysis"
  );
  const [defineTenants, setDefineTenants] = useState<TenantSummary[]>([]);
  const [defineExpanded, setDefineExpanded] = useState<string | null>(
    targetTenant
  );
  const [edits, setEdits] = useState<
    Record<string, {
      lease_type: string;
      cam_structure: CamStructure;
      pro_rata_share?: number | null;
      pool_reimbursements?: PoolReimbursement[];
    }>
  >({});
  const [saving, setSaving] = useState<string | null>(null);
  const [saved, setSaved] = useState<Set<string>>(new Set());
  // C3: Bulk selection state
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  // C1: AI lease analysis hint banner
  const [showLeaseHint, setShowLeaseHint] = useState(true);

  // Load scenarios
  useEffect(() => {
    if (!dealId) return;
    fetchScenarios(dealId)
      .then((s) => {
        setScenarios(s);
        const baseCase = s.find((sc) => sc.projection_result && sc.name === "Base Case");
        const withResult = baseCase || s.find((sc) => sc.projection_result);
        if (withResult) setSelectedId(withResult.id);
      })
      .catch((e) =>
        setError(
          e instanceof Error ? e.message : "Failed to load reimbursement data"
        )
      )
      .finally(() => setLoading(false));
  }, [dealId]);

  // Load tenants for Define mode
  const loadDefineTenants = useCallback(async () => {
    if (!dealId) return;
    const prop = deal.property_data;
    if (prop?.tenants?.length) {
      setDefineTenants(
        prop.tenants.map((t) => ({
          tenant_id: t.tenant_id || "",
          tenant_name: t.tenant_name || "",
          suite_id: t.suite_id || null,
          leased_sf: t.leased_sf || 0,
          lease_type: t.lease_type || "pending",
          cam_structure: t.cam_structure || {
            cam_type: "gross",
          },
          pro_rata_share: t.pro_rata_share ?? null,
          pool_reimbursements: (t as unknown as Record<string, unknown>).pool_reimbursements as PoolReimbursement[] ?? [],
        }))
      );
      return;
    }
    // Fall back to parsed documents
    try {
      const docs = await fetchDocuments(dealId);
      const rr = docs.find(
        (d: DocumentMeta) =>
          d.file_type === "rent_roll" &&
          d.parse_status === "complete" &&
          d.parse_result
      );
      if (!rr?.parse_result) return;
      const pr = rr.parse_result as Record<string, unknown>;
      const raw = (pr.tenants as Array<Record<string, unknown>>) ?? [];
      setDefineTenants(
        raw
          .map((r) => {
            const td = (r.tenant as Record<string, unknown>) ?? r;
            if (!td.tenant_name) return null;
            return {
              tenant_id: (td.tenant_id as string) || "",
              tenant_name: (td.tenant_name as string) || "",
              suite_id: (td.suite_id as string) || null,
              leased_sf: (td.leased_sf as number) || 0,
              lease_type: (td.lease_type as string) || "pending",
              cam_structure: (td.cam_structure as CamStructure) || {
                cam_type: "gross",
              },
            };
          })
          .filter((t): t is TenantSummary => t !== null)
      );
    } catch {
      // ignore
    }
  }, [dealId, deal.property_data]);

  useEffect(() => {
    loadDefineTenants();
  }, [loadDefineTenants]);

  function getEditedCam(t: TenantSummary): CamStructure {
    return edits[t.tenant_id]?.cam_structure ?? t.cam_structure;
  }

  function getEditedLeaseType(t: TenantSummary): string {
    return edits[t.tenant_id]?.lease_type ?? t.lease_type;
  }

  function updateEdit(tenantId: string, field: string, value: unknown) {
    setEdits((prev) => {
      const tenant = defineTenants.find((t) => t.tenant_id === tenantId);
      if (!tenant) return prev;
      const existing = prev[tenantId] ?? {
        lease_type: tenant.lease_type,
        cam_structure: { ...tenant.cam_structure },
        pro_rata_share: tenant.pro_rata_share ?? null,
        pool_reimbursements: tenant.pool_reimbursements ?? [],
      };
      if (field === "lease_type") {
        return {
          ...prev,
          [tenantId]: { ...existing, lease_type: value as string },
        };
      }
      if (field === "pro_rata_share") {
        return {
          ...prev,
          [tenantId]: { ...existing, pro_rata_share: value as number | null },
        };
      }
      if (field === "pool_reimbursements") {
        return {
          ...prev,
          [tenantId]: { ...existing, pool_reimbursements: value as PoolReimbursement[] },
        };
      }
      return {
        ...prev,
        [tenantId]: {
          ...existing,
          cam_structure: { ...existing.cam_structure, [field]: value },
        },
      };
    });
    // Remove from saved set when editing
    setSaved((prev) => {
      const next = new Set(prev);
      next.delete(tenantId);
      return next;
    });
  }

  function setCamMethod(tenantId: string, camType: string) {
    const leaseTypeMap: Record<string, string> = {
      gross: "gross",
      NNN: "NNN",
      base_year_stop: "modified_gross",
      expense_stop: "modified_gross",
      expense_cap: "NNN",
      fixed_psf: "NNN",
    };
    updateEdit(tenantId, "cam_type", camType);
    updateEdit(tenantId, "lease_type", leaseTypeMap[camType] || "NNN");
    // Also set sensible defaults for tax/insurance based on method
    if (camType === "gross") {
      updateEdit(tenantId, "tax_reimbursement", false);
      updateEdit(tenantId, "tax_reimbursement_type", "none");
      updateEdit(tenantId, "insurance_reimbursement", false);
      updateEdit(tenantId, "insurance_reimbursement_type", "none");
    } else if (camType === "expense_stop") {
      // Passthrough tax/insurance fold into the stopped obligation
      updateEdit(tenantId, "tax_reimbursement", true);
      updateEdit(tenantId, "tax_reimbursement_type", "NNN");
      updateEdit(tenantId, "insurance_reimbursement", true);
      updateEdit(tenantId, "insurance_reimbursement_type", "NNN");
    } else if (camType === "NNN") {
      updateEdit(tenantId, "tax_reimbursement", true);
      updateEdit(tenantId, "tax_reimbursement_type", "NNN");
      updateEdit(tenantId, "insurance_reimbursement", true);
      updateEdit(tenantId, "insurance_reimbursement_type", "NNN");
    }
  }

  async function saveTenant(tenantId: string) {
    const edit = edits[tenantId];
    if (!edit || !dealId) return;
    setSaving(tenantId);
    try {
      await updateTenantReimbursement(dealId, tenantId, {
        lease_type: edit.lease_type,
        cam_structure: edit.cam_structure,
        pro_rata_share: edit.pro_rata_share,
        pool_reimbursements: edit.pool_reimbursements as Array<Record<string, unknown>> | undefined,
      });
      setSaved((prev) => new Set(prev).add(tenantId));
      addToast("Reimbursement settings saved", "success");
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSaving(null);
    }
  }

  // C3: Bulk actions
  function bulkToggleId(id: string) {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function bulkSelectAll() {
    setBulkSelectedIds(new Set(defineTenants.map((t) => t.tenant_id)));
  }
  function bulkDeselectAll() {
    setBulkSelectedIds(new Set());
  }
  function bulkSetCamType(camType: string, tenantIds: string[]) {
    for (const tid of tenantIds) {
      setCamMethod(tid, camType);
    }
  }

  // Compute total leasable area with fallback from tenants
  const totalSf = useMemo(() => {
    const tla = deal.property_data?.total_leasable_area;
    if (tla && (tla as number) > 0) {
      return tla as number;
    }
    const tenants = (deal.property_data?.tenants ?? []) as unknown as Array<{ leased_sf?: number; square_footage?: number }>;
    return tenants.reduce((sum, t) => sum + (t.leased_sf || t.square_footage || 0), 0);
  }, [deal.property_data]);

  const hasRentRoll = !!((deal.property_data?.tenants ?? []) as unknown[]).length;

  if (loading) return <SkeletonTable rows={6} cols={5} />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <p className="text-red-700 font-medium mb-2">
          Failed to load reimbursement data
        </p>
        <p className="text-red-500 text-sm mb-4">{error}</p>
        <button
          onClick={() => { setError(null); setLoading(true); }}
          className="px-4 py-2 text-sm font-medium text-white bg-forest-600 rounded-lg hover:bg-forest-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Print header */}
      <div className="print-header hidden print:block">
        <h1>{deal.name || "Reimbursement Detail"}</h1>
        <p>Reimbursement Analysis</p>
      </div>

      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-bark-800">
            Reimbursement Analysis
          </h2>
          <p className="text-sm text-bark-400 mt-1">
            Per-tenant CAM, tax, and insurance recovery configuration and detail
          </p>
        </div>
        <Link
          to={`/deals/${dealId}/audit?tab=reimbursements`}
          className="print:hidden shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-forest-700 bg-forest-50 border border-forest-200 rounded-lg hover:bg-forest-100 transition-all"
        >
          Calculation Audit
        </Link>
      </div>

      {/* GLA=0 warning */}
      {totalSf <= 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3">
          <p className="text-sm text-red-700 font-semibold">
            Total Leasable Area (GLA) is zero or missing.
          </p>
          <p className="text-sm text-red-600 mt-1">
            {!hasRentRoll
              ? "Upload a rent roll to calculate the property's total leasable area."
              : <>No leasable area could be determined from the rent roll. Check that tenants have square footage values on the{" "}
                  <Link to={`/deals/${dealId}/rent-roll`} className="underline font-medium hover:text-red-800">Rent Roll</Link> tab.</>
            }
          </p>
        </div>
      )}

      {/* Mode tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-grove-100 rounded-lg p-1">
          <button
            onClick={() => setMode("analysis")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              mode === "analysis"
                ? "bg-white text-bark-700 shadow-sm"
                : "text-bark-400 hover:text-bark-600"
            }`}
          >
            <Calculator className="w-3.5 h-3.5 inline mr-1.5" />
            Analysis
          </button>
          <button
            onClick={() => setMode("define")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              mode === "define"
                ? "bg-white text-bark-700 shadow-sm"
                : "text-bark-400 hover:text-bark-600"
            }`}
          >
            <Settings className="w-3.5 h-3.5 inline mr-1.5" />
            Tenant Setup
          </button>
          <button
            onClick={() => setMode("pools")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              mode === "pools"
                ? "bg-white text-bark-700 shadow-sm"
                : "text-bark-400 hover:text-bark-600"
            }`}
          >
            <Download className="w-3.5 h-3.5 inline mr-1.5" />
            Expense Pools
          </button>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button
            onClick={() => {
              const scenario = scenarios.find((s) => s.id === selectedId);
              const details = scenario?.projection_result?.reimbursement_details as ReimbursementDetail[] | undefined;
              if (!details || details.length === 0) return;
              const tenantMap = new Map<string, { name: string; years: ReimbursementDetail[] }>();
              for (const d of details) {
                if (!tenantMap.has(d.tenant_id)) tenantMap.set(d.tenant_id, { name: d.tenant_name, years: [] });
                tenantMap.get(d.tenant_id)!.years.push(d);
              }
              tenantMap.forEach((v) => v.years.sort((a, b) => a.year - b.year));
              const tenantEntries = Array.from(tenantMap.entries());
              const yrs = tenantEntries[0]?.[1].years.map((y) => y.year) ?? [];
              const propTenants = (deal.property_data?.tenants ?? []) as unknown as Array<Record<string, unknown>>;
              const headers: (string | number)[] = ["Tenant Name", "Suite", "SF", "Lease Type", "Pro-Rata Share", ...yrs.map((yr) => `Year ${yr}`)];
              const rows = tenantEntries.map(([tid, data]) => {
                const info = propTenants.find((t) => t.tenant_id === tid);
                const camType = (info?.cam_structure as Record<string, unknown>)?.cam_type ?? "";
                return [
                  data.name,
                  (info?.suite_id as string) || "",
                  (info?.leased_sf as number) || "",
                  camType ? fmtCamType(String(camType)) : "",
                  data.years[0]?.pro_rata_share != null ? fmtPercent(data.years[0].pro_rata_share, 1) : "",
                  ...data.years.map((y) => Math.round(y.total_reimbursement)),
                ];
              });
              downloadCSV([headers, ...rows], `${deal.name || "reimbursements"}_reimbursements.csv`);
            }}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        </div>
      </div>

      {mode === "define" ? (
        <>
          {/* C1: AI Lease Analysis callout */}
          {showLeaseHint && (
            <div className="bg-forest-50 border border-forest-200 rounded-lg p-4 mb-4 flex items-start gap-3">
              <FileText className="w-5 h-5 text-forest-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-forest-800">Auto-configure reimbursements from leases</p>
                <p className="text-xs text-forest-600 mt-0.5">
                  Upload tenant lease PDFs to automatically extract NNN, base year stop,
                  expense cap, and other recovery structures. Expand any tenant row to get started.
                </p>
              </div>
              <button onClick={() => setShowLeaseHint(false)} className="text-forest-400 hover:text-forest-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {/* C3: Bulk action toolbar */}
          {defineTenants.length > 0 && (
            <BulkReimbursementToolbar
              tenants={defineTenants}
              selectedIds={bulkSelectedIds}
              onToggleId={bulkToggleId}
              onSelectAll={bulkSelectAll}
              onDeselectAll={bulkDeselectAll}
              onBulkSetCamType={bulkSetCamType}
            />
          )}
          <ReimbursementEditor
            tenants={defineTenants}
            expandedId={defineExpanded}
            onToggle={(id) =>
              setDefineExpanded(defineExpanded === id ? null : id)
            }
            getEditedCam={getEditedCam}
            getEditedLeaseType={getEditedLeaseType}
            updateEdit={updateEdit}
            setCamMethod={setCamMethod}
            saveTenant={saveTenant}
            saving={saving}
            saved={saved}
            edits={edits}
            dealId={dealId || ""}
            deal={deal}
            bulkSelectedIds={bulkSelectedIds}
            onBulkToggleId={bulkToggleId}
          />
          <CustomPoolsSection deal={deal} dealId={dealId || ""} />
        </>
      ) : mode === "pools" ? (
        <ExpensePoolsView deal={deal} dealId={dealId || ""} onDealUpdate={setDeal} />
      ) : (
        <AnalysisView
          scenarios={scenarios}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          expandedTenant={expandedTenant}
          setExpandedTenant={setExpandedTenant}
          deal={deal}
          dealId={dealId || ""}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Custom Expense Pools                                                       */
/* -------------------------------------------------------------------------- */

interface PoolDef {
  pool_id: string;
  pool_name: string;
  source_categories: string[];
}

function CustomPoolsSection({ deal, dealId }: { deal: Deal; dealId: string }) {
  const [pools, setPools] = useState<PoolDef[]>(
    (deal.property_data as unknown as Record<string, unknown>)?.expense_pool_definitions as PoolDef[] || []
  );
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  // Gather available expense line items from operating statement
  const opex = deal.property_data?.operating_expenses?.[0];
  const availableSources: { key: string; label: string }[] = [];
  if (opex) {
    const categories = [
      { key: "utilities" as const, label: "Utilities" },
      { key: "repairs_and_maintenance" as const, label: "Repairs & Maintenance" },
      { key: "janitorial" as const, label: "Janitorial" },
      { key: "landscaping" as const, label: "Landscaping" },
      { key: "management_fee" as const, label: "Management Fee" },
      { key: "administrative" as const, label: "Administrative" },
    ];
    for (const cat of categories) {
      const val = opex[cat.key];
      if (val && val > 0) {
        availableSources.push(cat);
      }
    }
    // Include 'other' line items
    if (opex.other) {
      for (const item of opex.other) {
        if (item.name && item.amount > 0) {
          availableSources.push({ key: `other:${item.name}`, label: item.name });
        }
      }
    }
  }

  const addPool = async () => {
    if (!newName.trim() || selectedSources.length === 0) return;
    const pool: PoolDef = {
      pool_id: `pool_${Date.now()}`,
      pool_name: newName.trim(),
      source_categories: selectedSources,
    };
    const updated = [...pools, pool];
    setPools(updated);
    setNewName("");
    setSelectedSources([]);
    setShowAdd(false);

    // Save to deal property_data
    try {
      const { updateDeal } = await import("../api/client");
      const propData = { ...deal.property_data, expense_pool_definitions: updated };
      await updateDeal(dealId, { property_data: propData });
    } catch {
      // ignore save errors
    }
  };

  const removePool = async (poolId: string) => {
    const updated = pools.filter((p) => p.pool_id !== poolId);
    setPools(updated);
    try {
      const { updateDeal } = await import("../api/client");
      const propData = { ...deal.property_data, expense_pool_definitions: updated };
      await updateDeal(dealId, { property_data: propData });
    } catch {
      // ignore
    }
  };

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
      <div className="px-6 py-5 border-b border-grove-200 flex items-center justify-between">
        <div>
          <h3 className="font-display text-sm font-semibold text-bark-700">
            Custom Expense Pools
          </h3>
          <p className="text-xs text-bark-300 mt-1">
            Create reimbursable expense pools from operating statement line items
          </p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="px-3 py-1.5 text-xs font-semibold text-white bg-forest-600 rounded-lg hover:bg-forest-500 transition-colors"
        >
          {showAdd ? "Cancel" : "Add Custom Pool"}
        </button>
      </div>

      {showAdd && (
        <div className="px-6 py-4 border-b border-grove-200 bg-grove-50">
          <div className="space-y-3">
            <Field label="Pool Name">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder='e.g. "Parking Lot", "Landscaping"'
                className="input-sm"
              />
            </Field>
            <div>
              <span className="text-[11px] font-medium text-bark-500 block mb-2">
                Source Expense Categories
              </span>
              <div className="flex flex-wrap gap-2">
                {availableSources.map((src) => (
                  <label
                    key={src.key}
                    className="flex items-center gap-1.5 text-xs text-bark-600 bg-white px-2 py-1 rounded border border-grove-200"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSources.includes(src.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSources((prev) => [...prev, src.key]);
                        } else {
                          setSelectedSources((prev) =>
                            prev.filter((s) => s !== src.key)
                          );
                        }
                      }}
                      className="rounded border-grove-300"
                    />
                    {src.label}
                  </label>
                ))}
              </div>
              {availableSources.length === 0 && (
                <p className="text-xs text-bark-400 italic">
                  Upload an operating statement to see available expense categories
                </p>
              )}
            </div>
            <div className="flex justify-end">
              <button
                onClick={addPool}
                disabled={!newName.trim() || selectedSources.length === 0}
                className="px-4 py-2 text-xs font-semibold text-white bg-forest-600 rounded-lg hover:bg-forest-500 disabled:opacity-50 transition-colors"
              >
                Create Pool
              </button>
            </div>
          </div>
        </div>
      )}

      {pools.length > 0 ? (
        <div className="divide-y divide-grove-200">
          {pools.map((pool) => (
            <div key={pool.pool_id} className="px-6 py-4 flex items-center justify-between">
              <div>
                <span className="font-medium text-sm text-bark-700">
                  {pool.pool_name}
                </span>
                <div className="text-xs text-bark-400 mt-0.5">
                  Sources: {pool.source_categories.map((s) =>
                    s.startsWith("other:") ? s.slice(6) : prettyFieldLabel(s)
                  ).join(", ")}
                </div>
              </div>
              <button
                onClick={() => removePool(pool.pool_id)}
                className="text-xs text-red-500 hover:text-red-700 font-medium"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="px-6 py-8 text-center text-sm text-bark-400">
          No custom pools defined. Click &ldquo;Add Custom Pool&rdquo; to create one.
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Analysis View — existing scenario reimbursement results                    */
/* -------------------------------------------------------------------------- */

function AnalysisView({
  scenarios,
  selectedId,
  setSelectedId,
  expandedTenant,
  setExpandedTenant,
  deal,
  dealId,
}: {
  scenarios: ScenarioResponse[];
  selectedId: string;
  setSelectedId: (id: string) => void;
  expandedTenant: string | null;
  setExpandedTenant: (id: string | null) => void;
  deal: Deal;
  dealId: string;
}) {
  // C5: PSF view mode
  const [viewMode, setViewMode] = useState<ViewMode>("total");

  const scenario = scenarios.find((s) => s.id === selectedId);
  const details = scenario?.projection_result?.reimbursement_details as
    | ReimbursementDetail[]
    | undefined;

  if (!details || details.length === 0) {
    return (
      <EmptyState
        icon={Calculator}
        title="No reimbursement data"
        description="Run a scenario to see per-tenant CAM calculations."
        action={
          <Link
            to={`/deals/${dealId}/scenarios`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md"
          >
            <Upload className="w-4 h-4" />
            Run Scenario
          </Link>
        }
      />
    );
  }

  const tenantMap = new Map<
    string,
    { name: string; years: ReimbursementDetail[] }
  >();
  for (const d of details) {
    if (!tenantMap.has(d.tenant_id)) {
      tenantMap.set(d.tenant_id, { name: d.tenant_name, years: [] });
    }
    tenantMap.get(d.tenant_id)!.years.push(d);
  }
  tenantMap.forEach((v) => v.years.sort((a, b) => a.year - b.year));
  const tenants = Array.from(tenantMap.entries());
  const years = tenants[0]?.[1].years.map((y) => y.year) ?? [];

  const yearTotals = years.map((yr) => {
    const total = details
      .filter((d) => d.year === yr)
      .reduce((s, d) => s + d.total_reimbursement, 0);
    return { year: yr, total };
  });

  const propTenants = (deal.property_data?.tenants ?? []) as unknown as Array<Record<string, unknown>>;

  // C5: PSF helper
  const totalSfAnalysis = propTenants.reduce((sum, t) => sum + ((t.leased_sf as number) || 0), 0);
  function fmtDollar(amount: number, tenantSf?: number): string {
    if (viewMode === "psf") {
      const sf = tenantSf ?? totalSfAnalysis;
      if (sf > 0) return fmtPSF(amount / sf);
    }
    return fmtCurrency(amount);
  }

  return (
    <>
      {/* Scenario selector */}
      {scenarios.filter((s) => s.projection_result).length > 1 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-bark-500">
            Scenario:
          </label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="px-4 py-2 text-sm bg-white border border-grove-300 rounded-lg text-bark-700"
          >
            {scenarios
              .filter((s) => s.projection_result)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </div>
      )}

      {/* C5: View mode toggle */}
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-bark-500">View:</span>
        <ViewModeToggle mode={viewMode} setMode={setViewMode} />
      </div>

      {/* Coverage metric cards */}
      {(() => {
        const acfs = scenario?.projection_result?.annual_cash_flows as Array<Record<string, number>> | undefined;
        const yr1 = acfs?.[0];
        if (!yr1) return null;
        const yr1Reimb = yearTotals[0]?.total ?? 0;
        const yr1Opex = yr1.total_operating_expenses ?? 0;
        const coverage = yr1Opex > 0 ? yr1Reimb / yr1Opex : 0;
        const netExposure = yr1Opex - yr1Reimb;
        return (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-grove-300 p-5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-bark-400 mb-1">Year 1 Recovery</p>
              <p className={`text-2xl font-semibold tabular-nums ${coverage > 1 ? 'text-red-600' : 'text-forest-700'}`}>{fmtPercent(coverage)}</p>
              <p className="text-[10px] text-bark-300 mt-0.5">of total operating expenses</p>
              {coverage > 1 && (
                <p className="text-[10px] text-red-600 mt-1">Reimbursement exceeds expenses — review data</p>
              )}
            </div>
            <div className="bg-white rounded-xl border border-grove-300 p-5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-bark-400 mb-1">Year 1 Reimbursement</p>
              <p className="text-2xl font-semibold text-bark-800 tabular-nums">{fmtCurrency(yr1Reimb)}</p>
              <p className="text-[10px] text-bark-300 mt-0.5">total tenant recoveries</p>
            </div>
            <div className="bg-white rounded-xl border border-grove-300 p-5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-bark-400 mb-1">Landlord Net Exposure</p>
              <p className="text-2xl font-semibold text-honey-700 tabular-nums">{fmtCurrency(netExposure)}</p>
              <p className="text-[10px] text-bark-300 mt-0.5">unreimbursed expenses</p>
            </div>
          </div>
        );
      })()}

      {/* Total reimbursement summary */}
      <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
        <div className="px-6 py-5 border-b border-grove-200">
          <h3 className="font-display text-sm font-semibold text-bark-700">
            Total Reimbursement Income by Year
          </h3>
        </div>
        <table className="w-full data-table">
          <thead>
            <tr>
              {years.map((yr) => (
                <th key={yr} className="num min-w-[100px]">
                  Year {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {yearTotals.map((yt) => (
                <td
                  key={yt.year}
                  className="num font-semibold text-forest-700"
                >
                  {fmtDollar(yt.total)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Per-tenant detail */}
      <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
        <div className="px-6 py-5 border-b border-grove-200">
          <h3 className="font-display text-sm font-semibold text-bark-700">
            Per-Tenant Reimbursement Detail
          </h3>
          <p className="text-xs text-bark-300 mt-1">
            Click a tenant to expand year-by-year detail
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full data-table text-[13px]">
            <thead>
              <tr>
                <th className="min-w-[200px]">Tenant</th>
                <th className="num">Pro Rata Share</th>
                {years.map((yr) => (
                  <th key={yr} className="num">
                    Yr {yr}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.map(([tid, data]) => {
                const isExpanded = expandedTenant === tid;
                const yr1 = data.years[0];
                const tenantInfo = propTenants.find(
                  (t) => t.tenant_id === tid
                );
                const camType =
                  (tenantInfo?.cam_structure as Record<string, unknown>)
                    ?.cam_type ?? "--";

                return (
                  <tbody key={tid}>
                    <tr
                      onClick={() =>
                        setExpandedTenant(isExpanded ? null : tid)
                      }
                      className="cursor-pointer hover:!bg-forest-50"
                    >
                      <td className="font-medium text-bark-700" data-field-id="lease_type" data-tenant={data.name}>
                        <div className="flex items-center gap-2" data-field-id="cam_structure" data-tenant={data.name}>
                          <span
                            className={`text-[10px] text-bark-300 transition-transform duration-200 ${
                              isExpanded ? "rotate-90" : ""
                            }`}
                          >
                            &#9656;
                          </span>
                          {data.name}
                          <CamBadge type={String(camType)} />
                        </div>
                      </td>
                      <td className="num">
                        {fmtPercent(yr1?.pro_rata_share)}
                      </td>
                      {data.years.map((y) => (
                        <td key={y.year} className="num">
                          {fmtDollar(y.total_reimbursement, (tenantInfo?.leased_sf as number) || 0)}
                        </td>
                      ))}
                    </tr>
                    {isExpanded &&
                      [
                        {
                          label: "CAM Reimbursement",
                          field: "cam_reimbursement" as const,
                        },
                        {
                          label: "Tax Reimbursement",
                          field: "tax_reimbursement" as const,
                        },
                        {
                          label: "Insurance Reimbursement",
                          field: "insurance_reimbursement" as const,
                        },
                        {
                          label: "CAM Pool Amount",
                          field: "cam_pool_amount" as const,
                        },
                      ].map((row) => (
                        <tr
                          key={`${tid}-${row.field}`}
                          className="bg-grove-50"
                        >
                          <td className="pl-10 text-bark-400 text-[12px]">
                            {row.label}
                          </td>
                          <td></td>
                          {data.years.map((y) => (
                            <td
                              key={y.year}
                              className="num text-bark-400 text-[12px]"
                            >
                              {fmtDollar(y[row.field], (tenantInfo?.leased_sf as number) || 0)}
                            </td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* C1: Recovery Rate Trend Chart */}
      <RecoveryRateTrendChart
        yearTotals={yearTotals}
        annualCashFlows={scenario?.projection_result?.annual_cash_flows as Array<Record<string, number>> | undefined}
      />

      {/* C2: Tenant Recovery Comparison Table */}
      <TenantRecoveryComparisonTable
        details={details}
        years={years}
        propTenants={propTenants}
        viewMode={viewMode}
      />

      {/* CAM Reconciliation */}
      <CamReconciliationTable details={details} years={years} />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* CAM Reconciliation Table                                                    */
/* -------------------------------------------------------------------------- */

function CamReconciliationTable({
  details,
  years,
}: {
  details: ReimbursementDetail[];
  years: number[];
}) {
  const hasReconciliation = details.some(
    (d) => d.cam_reconciliation && d.cam_reconciliation.actual_cam > 0
  );
  if (!hasReconciliation) return null;

  // Group by year, then by tenant
  const byYear = years
    .map((yr) => {
      const yearDetails = details.filter(
        (d) => d.year === yr && d.cam_reconciliation
      );
      if (yearDetails.length === 0) return null;
      return { year: yr, tenants: yearDetails };
    })
    .filter(Boolean) as Array<{
    year: number;
    tenants: ReimbursementDetail[];
  }>;

  if (byYear.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
      <div className="px-6 py-5 border-b border-grove-200">
        <h3 className="font-display text-sm font-semibold text-bark-700">
          CAM Reconciliation
        </h3>
        <p className="text-xs text-bark-300 mt-1">
          Estimated vs. actual CAM charges and tenant true-up amounts
        </p>
      </div>
      <div className="divide-y divide-grove-200">
        {byYear.map(({ year, tenants }) => (
          <div key={year} className="px-6 py-4">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-bark-500 mb-3">
              CAM Reconciliation &mdash; Year {year}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full data-table text-[13px]">
                <thead>
                  <tr>
                    <th className="min-w-[200px]">Tenant</th>
                    <th className="num">Estimated</th>
                    <th className="num">Actual</th>
                    <th className="num">True-Up</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((d) => {
                    const recon = d.cam_reconciliation!;
                    const trueUpColor =
                      recon.true_up_amount > 0
                        ? "text-forest-700"
                        : recon.true_up_amount < 0
                        ? "text-red-600"
                        : "text-bark-400";
                    return (
                      <tr key={d.tenant_id}>
                        <td className="font-medium text-bark-700">
                          {d.tenant_name}
                        </td>
                        <td className="num">
                          {fmtCurrency(recon.estimated_cam)}
                        </td>
                        <td className="num">
                          {fmtCurrency(recon.actual_cam)}
                        </td>
                        <td className={`num font-medium ${trueUpColor}`}>
                          {fmtCurrencyAcct(recon.true_up_amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

